import { receiptService } from '../../apiServices/receiptService';
import { Receipt } from '../../apiServices/types';
import { SyncReceiptRequest } from './types';

/**
 * Спрощений сервіс синхронізації без IndexedDB
 * Версія на сервері - єдина source of truth
 * Локальна версія зберігається в React state
 */
class SyncService {
  private syncInProgress: Set<string> = new Set();
  private pollingInProgress: Set<string> = new Set(); // Блокування для polling - щоб не запускати нову ітерацію поки попередня не завершиться
  private notificationCallback?: (message: string, type: 'success' | 'error' | 'warning' | 'info', duration?: number) => void;
  private statusChangeCallbacks: Set<() => void> = new Set();
  private pollingIntervals: Map<string, NodeJS.Timeout> = new Map();
  private versionWarnings: Map<string, boolean> = new Map(); // Чи показували вже warning для цього receipt
  
  private readonly POLLING_INTERVAL = 2000; // 2 секунди
  private readonly RETRY_DELAY = 2000; // Фіксована затримка 2 секунди між спробами
  
  constructor() {
    this.setupOnlineListener();
  }
  
  /**
   * Встановити callback для сповіщень
   */
  setNotificationCallback(callback: (message: string, type: 'success' | 'error' | 'warning' | 'info', duration?: number) => void) {
    this.notificationCallback = callback;
  }
  
  /**
   * Підписатися на зміни статусу синхронізації
   */
  onStatusChange(callback: () => void): () => void {
    this.statusChangeCallbacks.add(callback);
    return () => {
      this.statusChangeCallbacks.delete(callback);
    };
  }
  
  /**
   * Сповістити всіх підписників про зміну статусу
   */
  private notifyStatusChange(): void {
    this.statusChangeCallbacks.forEach(callback => {
      try {
        callback();
      } catch (error) {
        console.error('[SyncService] Error in status change callback:', error);
      }
    });
  }
  
  /**
   * Отримати унікальний ID клієнта
   */
  getClientId(): string {
    let clientId = localStorage.getItem('sync-client-id');
    if (!clientId) {
      clientId = `client_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
      localStorage.setItem('sync-client-id', clientId);
    }
    return clientId;
  }

  /**
   * Отримати clientVersion з pending changes (публічний метод для компонентів)
   */
  getPendingClientVersion(receiptId: string): number | null {
    return this.getPendingClientVersionInternal(receiptId);
  }
  
  /**
   * Retry з фіксованою затримкою
   * Не показує помилки користувачу - просто повторює запит кожні 2 секунди
   */
  private async retryWithFixedDelay<T>(
    operation: () => Promise<T>,
    operationName: string,
    receiptId?: string
  ): Promise<T> {
    while (true) {
      try {
        // Якщо офлайн - чекати поки з'явиться мережа
        if (!navigator.onLine) {
          await this.waitForOnline();
        }
        
        return await operation();
      } catch (error) {
        const errorMessage = error instanceof Error ? error.message : String(error);
        const logPrefix = receiptId ? `[SyncService] ${operationName} for ${receiptId}` : `[SyncService] ${operationName}`;
        console.log(`${logPrefix} failed, retrying in ${this.RETRY_DELAY}ms:`, errorMessage);
        
        // Чекати фіксовану затримку перед наступною спробою
        await this.sleep(this.RETRY_DELAY);
        // Продовжити цикл і спробувати знову
      }
    }
  }
  
  /**
   * Чекати поки з'явиться мережа
   */
  private waitForOnline(): Promise<void> {
    return new Promise((resolve) => {
      if (navigator.onLine) {
        resolve();
        return;
      }
      
      const checkOnline = () => {
        if (navigator.onLine) {
          window.removeEventListener('online', checkOnline);
          resolve();
        }
      };
      
      window.addEventListener('online', checkOnline);
    });
  }
  
  /**
   * Sleep для затримки
   */
  private sleep(ms: number): Promise<void> {
    return new Promise(resolve => setTimeout(resolve, ms));
  }
  
  /**
   * Почати polling версії для receipt
   */
  startPolling(receiptId: string, getCurrentClientVersion: () => number, onVersionConflict: (serverVersion: number) => void, onSyncNeeded?: () => Promise<SyncReceiptRequest | null>, onSyncSuccess?: (receipt: Receipt) => void): void {
    // Зупинити попередній polling якщо є
    this.stopPolling(receiptId);
    
    // Скинути warning для нового polling
    this.versionWarnings.delete(receiptId);
    
    const interval = setInterval(async () => {
      if (!navigator.onLine) {
        return;
      }
      
      // Перевірити чи вже виконується polling для цього receipt
      if (this.pollingInProgress.has(receiptId)) {
        return; // Пропустити цю ітерацію, поки попередня не завершиться
      }
      
      // Позначити що polling почався
      this.pollingInProgress.add(receiptId);
      
      try {
        // Виконати запит на версію (без retry - retry буде всередині запиту через apiClient)
        let serverVersion: number;
        try {
          serverVersion = await receiptService.getVersion(receiptId);
        } catch (error) {
          // Якщо запит не вдався - просто пропустити цю ітерацію
          // Наступна ітерація спробує знову через 2 секунди
          return; // Блокування буде звільнено в finally
        }
        
        const currentClientVersion = getCurrentClientVersion();
        
        // Якщо serverVersion > clientVersion - показати warning
        if (serverVersion > currentClientVersion) {
          // Показати warning тільки один раз
          if (!this.versionWarnings.has(receiptId)) {
            this.versionWarnings.set(receiptId, true);
            onVersionConflict(serverVersion);
          }
          return; // Блокування буде звільнено в finally
        }
        
        // Якщо clientVersion > serverVersion - відправити зміни
        if (currentClientVersion > serverVersion) {
          // Перевірити чи вже синхронізується (щоб уникнути дублікатів)
          if (this.syncInProgress.has(receiptId)) {
            return; // Вже синхронізується, чекаємо наступної перевірки
          }
          
          // Позначити що синхронізація почалася (захист від race condition)
          this.syncInProgress.add(receiptId);
          
          try {
            // Скинути warning якщо є
            this.versionWarnings.delete(receiptId);
            
            // Перевірити чи є pending changes в localStorage
            const pendingReceipt = this.getPendingChangesInternal(receiptId);
            const pendingVersion = this.getPendingClientVersionInternal(receiptId);
            
            let syncRequest: SyncReceiptRequest | null = null;
            
            if (pendingReceipt) {
              // Використати pending changes з localStorage
              syncRequest = pendingReceipt;
            } else if (onSyncNeeded) {
              // Якщо немає pending changes, але є callback - викликати його
              syncRequest = await onSyncNeeded();
            }
            
            if (syncRequest) {
              // Синхронізувати з версією з localStorage або поточною версією
              const versionToUse = pendingVersion !== null && pendingVersion !== undefined ? pendingVersion : currentClientVersion;
              
              // Відправити зміни на сервер (тільки якщо онлайн)
              if (navigator.onLine) {
                // Виконати sync запит (без retry - retry буде всередині запиту через apiClient)
                try {
                  const syncedReceipt = await receiptService.sync(receiptId, syncRequest);
                  
                  // Видалити pending changes після успішної синхронізації
                  this.clearPendingChanges(receiptId);
                  
                  // Передати весь receipt в callback для оновлення статусу
                  if (onSyncSuccess) {
                    onSyncSuccess(syncedReceipt);
                  }
                  
                  this.notifyStatusChange();
                } catch (error) {
                  // Якщо sync не вдався - зберегти в pending для наступної спроби
                  this.savePendingChanges(receiptId, syncRequest, versionToUse);
                  console.error(`[SyncService] Error syncing receipt ${receiptId}:`, error);
                }
              } else {
                // Офлайн - зберегти в localStorage
                this.savePendingChanges(receiptId, syncRequest, versionToUse);
              }
            }
          } catch (error) {
            console.error(`[SyncService] Error syncing receipt ${receiptId}:`, error);
            // При помилці - зберегти в pending якщо офлайн
            if (!navigator.onLine) {
              const pendingReceipt = this.getPendingChangesInternal(receiptId);
              const pendingVersion = this.getPendingClientVersionInternal(receiptId);
              if (pendingReceipt) {
                this.savePendingChanges(receiptId, pendingReceipt, pendingVersion || undefined);
              }
            }
          } finally {
            // Звільнити блокування
            this.syncInProgress.delete(receiptId);
          }
        }
        
        // Якщо рівні - нічого не робимо
      } catch (error) {
        // Неочікувана помилка
        console.error(`[SyncService] Unexpected error in polling for ${receiptId}:`, error);
      } finally {
        // Звільнити блокування після завершення ітерації
        this.pollingInProgress.delete(receiptId);
      }
    }, this.POLLING_INTERVAL);
    
    this.pollingIntervals.set(receiptId, interval);
  }
  
  /**
   * Зупинити polling версії для receipt
   */
  stopPolling(receiptId: string): void {
    const interval = this.pollingIntervals.get(receiptId);
    if (interval) {
      clearInterval(interval);
      this.pollingIntervals.delete(receiptId);
    }
    this.versionWarnings.delete(receiptId);
    this.pollingInProgress.delete(receiptId); // Звільнити блокування при зупинці polling
  }
  
  /**
   * Зберегти pending changes в localStorage (для офлайн режиму)
   * Публічний метод для компонентів
   */
  savePendingChanges(receiptId: string, receipt: SyncReceiptRequest, clientVersion?: number): void {
    try {
      const key = `sync-pending-${receiptId}`;
      localStorage.setItem(key, JSON.stringify({
        receiptId,
        receipt,
        clientVersion,
        timestamp: new Date().toISOString()
      }));
    } catch (error) {
      console.error(`[SyncService] Error saving pending changes for ${receiptId}:`, error);
    }
  }

  /**
   * Отримати clientVersion з pending changes (приватний метод)
   */
  private getPendingClientVersionInternal(receiptId: string): number | null {
    try {
      const key = `sync-pending-${receiptId}`;
      const data = localStorage.getItem(key);
      if (data) {
        const parsed = JSON.parse(data);
        return parsed.clientVersion || null;
      }
    } catch (error) {
      console.error(`[SyncService] Error getting pending client version for ${receiptId}:`, error);
    }
    return null;
  }

  /**
   * Отримати pending changes з localStorage (приватний метод)
   */
  private getPendingChangesInternal(receiptId: string): SyncReceiptRequest | null {
    try {
      const key = `sync-pending-${receiptId}`;
      const data = localStorage.getItem(key);
      if (data) {
        const parsed = JSON.parse(data);
        return parsed.receipt;
      }
    } catch (error) {
      console.error(`[SyncService] Error getting pending changes for ${receiptId}:`, error);
    }
    return null;
  }

  /**
   * Отримати pending changes з localStorage (публічний метод для компонентів)
   */
  getPendingChanges(receiptId: string): SyncReceiptRequest | null {
    return this.getPendingChangesInternal(receiptId);
  }

  /**
   * Видалити pending changes з localStorage
   */
  private clearPendingChanges(receiptId: string): void {
    try {
      const key = `sync-pending-${receiptId}`;
      localStorage.removeItem(key);
    } catch (error) {
      console.error(`[SyncService] Error clearing pending changes for ${receiptId}:`, error);
    }
  }

  /**
   * Отримати всі receiptId з pending changes
   */
  private getAllPendingReceiptIds(): string[] {
    const receiptIds: string[] = [];
    try {
      for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i);
        if (key && key.startsWith('sync-pending-')) {
          const receiptId = key.replace('sync-pending-', '');
          receiptIds.push(receiptId);
        }
      }
    } catch (error) {
      console.error('[SyncService] Error getting all pending receipt IDs:', error);
    }
    return receiptIds;
  }

  /**
   * Синхронізувати receipt (відправити зміни на сервер)
   * Викликається ТІЛЬКИ з polling коли clientVersion > serverVersion
   */
  async syncReceipt(receiptId: string, receipt: SyncReceiptRequest, clientVersion?: number): Promise<Receipt> {
    if (!navigator.onLine) {
      // Офлайн - зберегти в localStorage для подальшої синхронізації
      this.savePendingChanges(receiptId, receipt, clientVersion);
      throw new Error('Offline');
    }
    
    try {
      // Відправити зміни на сервер
      const syncedReceipt = await receiptService.sync(receiptId, receipt);
      
      // Видалити pending changes після успішної синхронізації
      this.clearPendingChanges(receiptId);
      
      this.notifyStatusChange();
      return syncedReceipt;
    } catch (error) {
      // Якщо помилка - зберегти в pending
      if (!navigator.onLine) {
        this.savePendingChanges(receiptId, receipt, clientVersion);
      }
      console.error(`[SyncService] Error syncing ${receiptId}:`, error);
      throw error;
    }
  }

  
  /**
   * Перевірити чи синхронізується зараз receipt
   */
  isSyncing(receiptId: string): boolean {
    return this.syncInProgress.has(receiptId);
  }
  
  /**
   * Налаштувати слухач онлайн/офлайн
   */
  private setupOnlineListener(): void {
    window.addEventListener('online', () => {
      console.log('[SyncService] Online');
      // Не викликаємо syncAllPendingChanges - polling сам перевірить версії і синхронізує коли потрібно
      this.notifyStatusChange();
    });
    
    window.addEventListener('offline', () => {
      console.log('[SyncService] Offline');
      this.notifyStatusChange();
    });
  }
}

export const syncService = new SyncService();
